import React, { useState } from "react"
import type { DateRange } from "react-day-picker"

import { Calendar as CalendarGrid, cn, Input, Tooltip } from "@coval/ui"
import { endOfDay, format, isSameDay, set, startOfDay } from "date-fns"

interface DateTimeRangePickerProps {
  dateOnly?: boolean
  startDate: Date | null
  endDate: Date | null
  onChange: (start: Date | null, end: Date | null) => void
  isLoading?: boolean
  className?: string
  /** Hide the "(Local)" timezone hint next to the time fields. */
  hideTimezoneHint?: boolean
}

interface DateTimePickerProps {
  value: Date | null
  onChange: (value: Date | null) => void
  isLoading?: boolean
  className?: string
  hideTimezoneHint?: boolean
  /**
   * The latest instant the field accepts. Later days leave the calendar, the
   * time field stops at it on the day it falls in, and any bound that would
   * still land past it is pulled back, so no entry path produces a later value.
   */
  maxDate?: Date
}

/**
 * Keeps the time already on a bound when the calendar moves its day, so
 * picking a new day never silently widens or narrows a window. Seconds and
 * milliseconds come along too: presets seed ends at 23:59:59.999, and dropping
 * that tail would quietly cut the last minute out of the range.
 */
function applyDayToBound(
  day: Date,
  bound: Date | null,
  fallback: (date: Date) => Date,
) {
  if (!bound) {
    return fallback(day)
  }
  return set(day, {
    hours: bound.getHours(),
    minutes: bound.getMinutes(),
    seconds: bound.getSeconds(),
    milliseconds: bound.getMilliseconds(),
  })
}

function formatTimeForInput(date: Date | null) {
  return date ? format(date, "HH:mm") : ""
}

const INVERTED_RANGE_MESSAGE = "End time must be after the start time."

/**
 * A time field reports an empty value while it is half-typed, so only a
 * complete time is worth pushing onto a bound.
 */
const COMPLETE_TIME = /^\d{2}:\d{2}$/

function applyTimeToBound(bound: Date, time: string) {
  const [hours, minutes] = time.split(":").map(Number)
  return set(bound, { hours, minutes, seconds: 0, milliseconds: 0 })
}

/**
 * Picks a date range: the calendar owns the days, the time fields refine the
 * hours. A bound with no time runs the whole day, which is what the presets
 * produce, so time entry stays optional on surfaces that never need it. The
 * end always lands strictly after the start, so an applied range spans time.
 */
export const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  startDate,
  endDate,
  dateOnly = false,
  onChange,
  isLoading = false,
  className = "",
  hideTimezoneHint = false,
}) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const [rangeError, setRangeError] = useState<string | null>(null)
  /** Only a same-day range can be inverted by a time: the calendar orders days. */
  const sharesOneDay = Boolean(
    startDate && endDate && isSameDay(startDate, endDate),
  )

  function handleRangeSelect(range: DateRange | undefined) {
    setRangeError(null)
    const nextStart = range?.from
      ? applyDayToBound(range.from, startDate, startOfDay)
      : null
    const nextEnd = range?.to
      ? applyDayToBound(range.to, endDate, endOfDay)
      : null
    /**
     * Carried times can collapse a multi-day range onto one day, leaving an
     * end at or before the start. A day picked that way runs whole, matching
     * what a day picked with no times on its bounds produces.
     */
    if (nextStart && nextEnd && nextEnd <= nextStart) {
      onChange(startOfDay(nextStart), endOfDay(nextEnd))
      return
    }
    onChange(nextStart, nextEnd)
  }

  function handleStartTimeChange(time: string) {
    if (!startDate) {
      return
    }
    const nextStart = applyTimeToBound(startDate, time)
    if (endDate && nextStart >= endDate) {
      setRangeError(INVERTED_RANGE_MESSAGE)
      return
    }
    setRangeError(null)
    onChange(nextStart, endDate)
  }

  function handleEndTimeChange(time: string) {
    if (!endDate) {
      return
    }
    const nextEnd = applyTimeToBound(endDate, time)
    if (startDate && nextEnd <= startDate) {
      setRangeError(INVERTED_RANGE_MESSAGE)
      return
    }
    setRangeError(null)
    onChange(startDate, nextEnd)
  }

  return (
    <div className="flex flex-col gap-3">
      <CalendarGrid
        mode="range"
        selected={
          startDate || endDate
            ? { from: startDate ?? undefined, to: endDate ?? undefined }
            : undefined
        }
        onSelect={handleRangeSelect}
        defaultMonth={startDate ?? endDate ?? undefined}
        disabled={isLoading}
        className="self-center p-0"
      />
      <div className={cn("flex flex-col gap-2", className)}>
        {dateOnly ? (
          <>
            <Input
              type="date"
              aria-label="Start date"
              value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
              disabled={isLoading}
              onChange={(event) =>
                onChange(
                  event.target.value
                    ? startOfDay(new Date(`${event.target.value}T12:00:00`))
                    : null,
                  endDate,
                )
              }
            />
            <Input
              type="date"
              aria-label="End date"
              value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
              disabled={isLoading}
              onChange={(event) =>
                onChange(
                  startDate,
                  event.target.value
                    ? endOfDay(new Date(`${event.target.value}T12:00:00`))
                    : null,
                )
              }
            />
          </>
        ) : (
          <>
            <BoundRow
              label="Start"
              date={startDate}
              isLoading={isLoading}
              max={sharesOneDay ? formatTimeForInput(endDate) : undefined}
              onTimeChange={handleStartTimeChange}
            />
            <BoundRow
              label="End"
              date={endDate}
              isLoading={isLoading}
              min={sharesOneDay ? formatTimeForInput(startDate) : undefined}
              onTimeChange={handleEndTimeChange}
            />
          </>
        )}
        {rangeError && (
          <p className="text-tiny text-coval-danger-text">{rangeError}</p>
        )}
        {!dateOnly && !hideTimezoneHint && (
          <Tooltip content={timeZone} side="top">
            <span className="self-end text-tiny text-coval-text-muted">
              Times are local
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  isLoading = false,
  className = "",
  hideTimezoneHint = false,
  maxDate,
}) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const disabledDays = maxDate ? { after: maxDate } : undefined
  /**
   * A time field only marks an out-of-range value invalid, so the bound is
   * clamped here as well: typing past the maximum lands on it instead.
   */
  const clampToMax = (next: Date | null) =>
    next && maxDate && next > maxDate ? maxDate : next

  function handleDaySelect(day: Date | undefined) {
    onChange(clampToMax(day ? applyDayToBound(day, value, startOfDay) : null))
  }

  function handleTimeChange(time: string) {
    if (!value) {
      return
    }
    onChange(clampToMax(applyTimeToBound(value, time)))
  }

  return (
    <div className="flex flex-col gap-3">
      <CalendarGrid
        mode="single"
        selected={value ?? undefined}
        onSelect={handleDaySelect}
        defaultMonth={value ?? undefined}
        disabled={isLoading || disabledDays}
        className="self-center p-0"
      />
      <div className={cn("flex flex-col gap-2", className)}>
        <BoundRow
          label="Time"
          date={value}
          isLoading={isLoading}
          max={
            maxDate && value && isSameDay(value, maxDate)
              ? formatTimeForInput(maxDate)
              : undefined
          }
          timeAriaLabel="Time"
          onTimeChange={handleTimeChange}
        />
        {!hideTimezoneHint && (
          <Tooltip content={timeZone} side="top">
            <span className="self-end text-tiny text-coval-text-muted">
              Times are local
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

/**
 * One bound of the range: the day the calendar picked, and the time field that
 * refines it. Without a day there is nothing to refine, so the field waits.
 *
 * The field holds what the user is typing rather than the bound's time, so an
 * emptied or half-entered value survives instead of snapping back mid-edit;
 * only a complete time reaches the bound, and an abandoned edit is restored on
 * blur so the field never disagrees with the bound it labels.
 */
function BoundRow({
  date,
  isLoading,
  label,
  max,
  min,
  onTimeChange,
  timeAriaLabel,
}: {
  date: Date | null
  isLoading: boolean
  label: string
  max?: string
  min?: string
  onTimeChange: (time: string) => void
  timeAriaLabel?: string
}) {
  const time = formatTimeForInput(date)
  const [draft, setDraft] = useState(time)
  const [appliedTime, setAppliedTime] = useState(time)

  if (time !== appliedTime) {
    setAppliedTime(time)
    setDraft(time)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDraft(e.target.value)
    if (COMPLETE_TIME.test(e.target.value)) {
      onTimeChange(e.target.value)
    }
  }

  function handleBlur() {
    if (!COMPLETE_TIME.test(draft)) {
      setDraft(time)
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col">
        <span className="text-tiny text-coval-text-muted">{label}</span>
        <span className="truncate text-sm text-coval-text">
          {date ? format(date, "MMM d, yyyy") : "Pick a date"}
        </span>
      </div>
      <Input
        type="time"
        value={draft}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isLoading || !date}
        min={min}
        max={max}
        className="w-32 shrink-0"
        aria-label={timeAriaLabel ?? `${label} time`}
      />
    </div>
  )
}
